import { useFPOverviewData } from '../../../hooks/useFPOverviewData';
import { useFPActionsData } from '../../../hooks/useFPActionsData';
import { useFPReferralsByServiceData } from '../../../hooks/useFPReferralsByServiceData';
import { useFPTopSearchesData } from '../../../hooks/useFPTopSearchesData';
import { useFPClientActionsData } from '../../../hooks/useFPClientActionsData';
import { useFPEngagementRateData } from '../../../hooks/useFPEngagementRateData';
import { useFPTopAdvisorsData } from '../../../hooks/useFPTopAdvisorsData';
import { useFPAdvisorRetentionData } from '../../../hooks/useFPAdvisorRetentionData';
import { useFPTrendingCategoriesData } from '../../../hooks/useFPTrendingCategoriesData';
import { useFPSearchVolumeData } from '../../../hooks/useFPSearchVolumeData';
import { useEngagementData } from '../../../hooks/useEngagementData';
import { useSearchData } from '../../../hooks/useSearchData';
import { ChartContainer } from '../shared/ChartContainer';
import { KPICardsSkeleton, ChartSkeleton } from '../shared/AnalyticsSkeletons';
import { EngagementTrendChart } from '../engagements/EngagementTrendChart';
import { SearchHeatmap } from '../search/SearchHeatmap';
import { FPKpiRow } from './overview/FPKpiRow';
import { ReferralValueBanner } from './overview/ReferralValueBanner';
import { AdvisorActionsBar } from './overview/AdvisorActionsBar';
import { ReferralsByServiceDonut } from './overview/ReferralsByServiceDonut';
import { TopSearchesBar } from './overview/TopSearchesBar';
import { TopClientActionsDonut } from './overview/TopClientActionsDonut';
import { AdvisorEngagementRateChart } from './overview/AdvisorEngagementRateChart';
import { TopAdvisorsLeaderboard } from './overview/TopAdvisorsLeaderboard';
import { AdvisorRetentionChart } from './overview/AdvisorRetentionChart';
import { TrendingCategoriesChart } from './overview/TrendingCategoriesChart';
import { SearchVolumeHeatmap } from './overview/SearchVolumeHeatmap';
import type { AnalyticsQueryParams } from '../../../types/analytics.types';

export const SectionError = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
    <p className="text-sm text-red-700 mb-3">{message}</p>
    <button type="button" onClick={onRetry} className="text-sm font-medium text-red-700 underline">
      Retry
    </button>
  </div>
);

export const OverviewKpis = ({
  overview,
  dateRange,
}: {
  overview: ReturnType<typeof useFPOverviewData>;
  dateRange?: AnalyticsQueryParams['dateRange'];
}) => {
  if (overview.isLoading) return <KPICardsSkeleton count={5} />;
  if (overview.isError)
    return <SectionError message="Failed to load FP KPIs." onRetry={() => overview.refetch()} />;
  if (!overview.data) return null;
  return <FPKpiRow data={overview.data} dateRange={dateRange} />;
};

export const ReferralValueSection = ({
  overview,
}: {
  overview: ReturnType<typeof useFPOverviewData>;
}) => {
  if (overview.isLoading) return <ChartSkeleton height="h-[140px]" />;
  if (overview.isError)
    return (
      <SectionError message="Failed to load referral value." onRetry={() => overview.refetch()} />
    );
  if (!overview.data) return null;
  return (
    <ReferralValueBanner
      value={overview.data.referralValue}
      activeAdvisors={overview.data.activeAdvisors}
    />
  );
};

export const ActionsSection = ({
  actions,
  firmSelected,
}: {
  actions: ReturnType<typeof useFPActionsData>;
  firmSelected: boolean;
}) => {
  if (actions.isLoading) return <ChartSkeleton />;
  if (actions.isError)
    return (
      <SectionError message="Failed to load advisor actions." onRetry={() => actions.refetch()} />
    );
  if (!actions.data) return null;
  return <AdvisorActionsBar actions={actions.data.actions} firmSelected={firmSelected} />;
};

export const TrendSection = ({
  engagement,
}: {
  engagement: ReturnType<typeof useEngagementData>;
}) => {
  if (engagement.isLoading) return <ChartSkeleton />;
  if (engagement.isError)
    return (
      <SectionError
        message="Failed to load engagement trend."
        onRetry={() => engagement.refetch()}
      />
    );
  if (!engagement.data) return null;
  return <EngagementTrendChart data={engagement.data.trend} />;
};

export const TopSearchesSection = ({
  topSearches,
}: {
  topSearches: ReturnType<typeof useFPTopSearchesData>;
}) => {
  if (topSearches.isLoading) return <ChartSkeleton />;
  if (topSearches.isError)
    return (
      <SectionError
        message="Failed to load top searches by category."
        onRetry={() => topSearches.refetch()}
      />
    );
  if (!topSearches.data) return null;
  return <TopSearchesBar categories={topSearches.data.categories} />;
};

export const ClientActionsSection = ({
  clientActions,
}: {
  clientActions: ReturnType<typeof useFPClientActionsData>;
}) => {
  if (clientActions.isLoading) return <ChartSkeleton />;
  if (clientActions.isError)
    return (
      <SectionError message="Failed to load client actions." onRetry={() => clientActions.refetch()} />
    );
  if (!clientActions.data) return null;
  return <TopClientActionsDonut actions={clientActions.data.actions} />;
};

// Clients can only search the SP directory, so care-seeker search demand by
// state == "SP searches by state" (the metric the team asked for), surfaced on
// the FP tab per the spec's dedupe/reuse guidance.
const CLIENT_SEARCH_TYPES = ['CARE_SEEKER'] as const;

export const ClientSearchGeoSection = ({
  search,
}: {
  search: ReturnType<typeof useSearchData>;
}) => {
  if (search.isLoading) return <ChartSkeleton />;
  if (search.isError)
    return (
      <SectionError
        message="Failed to load client search geography."
        onRetry={() => search.refetch()}
      />
    );
  if (!search.data) return null;
  // Header total must reflect the CARE_SEEKER slice shown, not the all-role total.
  const clientTotal = search.data.states.reduce(
    (sum, s) => sum + (s.byUserType?.CARE_SEEKER ?? 0),
    0,
  );
  return (
    <ChartContainer
      title="Client Searches by State"
      subtitle="Where clients search for service providers (all firms)"
    >
      <SearchHeatmap
        data={search.data.states}
        totalSearches={clientTotal}
        activeUserTypes={CLIENT_SEARCH_TYPES}
      />
    </ChartContainer>
  );
};

export const EngagementRateSection = ({
  engagementRate,
}: {
  engagementRate: ReturnType<typeof useFPEngagementRateData>;
}) => {
  if (engagementRate.isLoading) return <ChartSkeleton />;
  if (engagementRate.isError)
    return (
      <SectionError
        message="Failed to load engagement rate trend."
        onRetry={() => engagementRate.refetch()}
      />
    );
  if (!engagementRate.data) return null;
  return <AdvisorEngagementRateChart points={engagementRate.data.points} />;
};

export const ReferralsSection = ({
  referrals,
}: {
  referrals: ReturnType<typeof useFPReferralsByServiceData>;
}) => {
  if (referrals.isLoading) return <ChartSkeleton />;
  if (referrals.isError)
    return (
      <SectionError
        message="Failed to load referrals by service."
        onRetry={() => referrals.refetch()}
      />
    );
  // Nothing fetched yet (e.g. query disabled): render nothing. The donut owns
  // the post-fetch empty/zero-category state.
  if (!referrals.data) return null;
  return <ReferralsByServiceDonut categories={referrals.data.categories} />;
};

export const TopAdvisorsSection = ({
  topAdvisors,
}: {
  topAdvisors: ReturnType<typeof useFPTopAdvisorsData>;
}) => {
  if (topAdvisors.isLoading) return <ChartSkeleton />;
  if (topAdvisors.isError)
    return (
      <SectionError message="Failed to load top advisors." onRetry={() => topAdvisors.refetch()} />
    );
  if (!topAdvisors.data) return null;
  return <TopAdvisorsLeaderboard advisors={topAdvisors.data.advisors} />;
};

export const RetentionSection = ({
  retention,
}: {
  retention: ReturnType<typeof useFPAdvisorRetentionData>;
}) => {
  if (retention.isLoading) return <ChartSkeleton />;
  if (retention.isError)
    return (
      <SectionError message="Failed to load advisor retention." onRetry={() => retention.refetch()} />
    );
  if (!retention.data) return null;
  return <AdvisorRetentionChart points={retention.data.points} />;
};

export const TrendingSection = ({
  trending,
}: {
  trending: ReturnType<typeof useFPTrendingCategoriesData>;
}) => {
  if (trending.isLoading) return <ChartSkeleton />;
  if (trending.isError)
    return (
      <SectionError
        message="Failed to load trending categories."
        onRetry={() => trending.refetch()}
      />
    );
  if (!trending.data) return null;
  return <TrendingCategoriesChart dates={trending.data.dates} series={trending.data.series} />;
};

export const SearchVolumeSection = ({
  searchVolume,
}: {
  searchVolume: ReturnType<typeof useFPSearchVolumeData>;
}) => {
  if (searchVolume.isLoading) return <ChartSkeleton />;
  if (searchVolume.isError)
    return (
      <SectionError
        message="Failed to load search volume."
        onRetry={() => searchVolume.refetch()}
      />
    );
  if (!searchVolume.data) return null;
  const { categories, states, matrix, max } = searchVolume.data;
  return <SearchVolumeHeatmap categories={categories} states={states} matrix={matrix} max={max} />;
};
